import { GroupedGroupOption, GroupOption } from '@bp/ui-components';
import type { _DivisionFragmentFragment, _GroupFragmentFragment } from '../../../types/planung-graphql-client-defs';
import type { AscUnresolvedEntity } from '../../../utils/timetable/timetableVersion';
import { ASC_MAPPING_SKIP } from './ascMapping';

/**
 * Auswahllisten für Gruppen-Zuordnungen des aSc-Imports. Gruppen hängen an zwei Achsen: sie gehören
 * zu einer Klasse UND zu genau einer Teilung. Beides muss die Auswahl einschränken — sonst würde man
 * eine Gruppe aus der falschen Klasse oder aus einer fremden Teilung verknüpfen, was das Backend
 * ohnehin wieder verwirft (s. `asc-entity-resolution.ts`).
 *
 * Die Gruppen-Zeilen laufen über `GroupPicker` (Single-Select), der die Optionen nach Teilung
 * verschachtelt erwartet (`GroupedGroupOption[]` = `{ label: Teilungsname, options: GroupOption[] }[]`).
 * So sieht man neben der Zielgruppe auch die übrigen Gruppen ihrer Teilung — genau die Kontextinfo,
 * die eine flache Namensliste nicht hergibt.
 *
 * Die Listen werden EINMAL aus den Stammdaten gebaut und bleiben referenzstabil; die Zuordnung „Zeile
 * -> Liste" übernimmt {@link ascGroupOptionsByEntry}, das nur die live getroffenen Auswahlen einrechnet.
 */
export type AscGroupOptions = {
  /** Gruppen je Klasse (uuid) — solange die Teilung der ASC-Teilung noch nicht feststeht. Eine
   * `GroupedGroupOption` je Teilung der Klasse. */
  byClass: Map<string, GroupedGroupOption[]>;
  /** Gruppen je Teilung (uuid) — sobald die Teilung feststeht, wird nur noch hieraus gewählt. */
  byDivision: Map<string, GroupedGroupOption[]>;
  /** Gruppe (uuid) -> Teilung (uuid). Über sie bestimmt eine Auswahl die Teilung der ASC-Teilung. */
  divisionOfGroup: Map<string, string>;
  /** Teilung (uuid) -> Anzeigename, für den Hinweis „nur Gruppen aus Teilung X". */
  divisionName: Map<string, string>;
};

export type AscGroupEntryOptions = {
  options: GroupedGroupOption[];
  /** Gesetzt, wenn die Auswahl auf genau eine Teilung begrenzt ist. */
  divisionName?: string;
};

/**
 * EIN Collator für alle Sortierungen dieser Datei. `localeCompare(a, b, options)` erzeugt pro Aufruf
 * implizit einen neuen — bei hunderten Optionen ist das der teuerste Einzelposten.
 */
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
const byGroupLabel = (a: GroupOption, b: GroupOption) => collator.compare(a.label, b.label);
const byDivisionLabel = (a: GroupedGroupOption, b: GroupedGroupOption) =>
  collator.compare(a.label ?? '', b.label ?? '');

/**
 * Baut die referenzstabilen Optionslisten aus den Stammdaten. Grundlage sind die Teilungen: jede
 * kennt ihre Gruppen (`groups`) und ihre Klasse. Daraus entstehen sowohl die
 * teilungs- als auch die klassenweiten Listen, jede Teilung wird zu einer `GroupedGroupOption`.
 *
 * @param groups liefert nur die Anzeigenamen — die Zugehörigkeit kommt aus den Teilungen.
 * @param skipGroup die „überspringen"-Pseudo-Teilung, die jeder Liste vorangestellt wird (`GroupPicker`
 *   kennt kein Skip-Sentinel, also reisen wir es als eigene Gruppe mit einer Option mit).
 */
export function buildAscGroupOptions(
  divisions: _DivisionFragmentFragment[],
  groups: _GroupFragmentFragment[],
  skipGroup: GroupedGroupOption,
): AscGroupOptions {
  const labelOf = new Map(groups.map((group) => [group.uuid, group.name]));

  const divisionName = new Map<string, string>();
  const divisionOfGroup = new Map<string, string>();
  const byDivision = new Map<string, GroupedGroupOption[]>();
  // Je Klasse die Teilungs-Gruppen sammeln — eine Gruppe gehört zu genau einer Teilung, also entsteht
  // ohne Zutun keine Dopplung (jede Teilung ist ihr eigenes Kopf-Element).
  const divisionsByClass = new Map<string, GroupedGroupOption[]>();

  for (const division of divisions) {
    divisionName.set(division.uuid, division.name);
    const classUuid = division.class?.uuid;

    const options: GroupOption[] = [];
    for (const divisionGroup of division.groups) {
      const groupUuid = divisionGroup.uuid;
      const label = labelOf.get(groupUuid);
      // Ohne Stammdatensatz gibt es kein Label — eine unbeschriftete Option wäre nicht wählbar.
      if (label === undefined) continue;
      divisionOfGroup.set(groupUuid, division.uuid);
      options.push({ value: groupUuid, label });
    }
    options.sort(byGroupLabel);

    const divisionGroup: GroupedGroupOption = { label: division.name, options };
    // Skip vorangestellt: die Liste je Teilung bleibt so referenzstabil (die Zeilen sind memoisiert).
    byDivision.set(division.uuid, [skipGroup, divisionGroup]);

    if (classUuid) {
      const list = divisionsByClass.get(classUuid) ?? [];
      list.push(divisionGroup);
      divisionsByClass.set(classUuid, list);
    }
  }

  const byClass = new Map<string, GroupedGroupOption[]>();
  for (const [classUuid, list] of divisionsByClass) {
    byClass.set(classUuid, [skipGroup, ...list.sort(byDivisionLabel)]);
  }

  return { byClass, byDivision, divisionOfGroup, divisionName };
}

/** Bindende Auswahl einer Zeile: die gewählte UUID, oder `undefined` bei „überspringen"/leer. */
function chosenGroup(mappings: Record<string, string | null>, ascId: string): string | undefined {
  const value = mappings[`GROUP:${ascId}`];
  return value && value !== ASC_MAPPING_SKIP ? value : undefined;
}

function bucket(map: Map<string, Set<string>>, key: string): Set<string> {
  let set = map.get(key);
  if (!set) {
    set = new Set();
    map.set(key, set);
  }
  return set;
}

/**
 * Gruppen mehrerer Teilungen zusammenführen (Sonderfall widersprüchlicher Bindungen). Jede Teilung
 * bleibt ihr eigenes Kopf-Element — die Verschachtelung ist genau der Kontext, den der Anwender hier
 * braucht.
 */
function mergeDivisions(
  divisions: Set<string>,
  groupOptions: AscGroupOptions,
  skipGroup: GroupedGroupOption,
): GroupedGroupOption[] {
  const merged: GroupedGroupOption[] = [];
  for (const divisionUuid of divisions) {
    for (const group of groupOptions.byDivision.get(divisionUuid) ?? []) {
      // Das vorangestellte Skip nicht je Teilung wiederholen.
      if (group === skipGroup) continue;
      merged.push(group);
    }
  }
  return [skipGroup, ...merged.sort(byDivisionLabel)];
}

/**
 * Sperrt die von Geschwister-Zeilen belegten Gruppen, statt sie zu entfernen: sie bleiben unter
 * ihrer Teilung sichtbar (die Teilung soll vollständig erkennbar sein), sind aber nicht wählbar —
 * zwei ASC-Gruppen derselben Teilung dürfen nicht dieselbe Gruppe treffen. Die eigene Wahl und
 * „überspringen" bleiben aktiv. Ist nichts gesperrt, bleiben Basisliste UND die unberührten
 * Teilungen referenzgleich — die Zeilen sind memoisiert.
 */
function disableTaken(
  base: GroupedGroupOption[],
  taken: Set<string> | undefined,
  own: string | undefined,
): GroupedGroupOption[] {
  if (!taken || taken.size === 0) return base;
  const isTaken = (option: GroupOption) => option.value !== own && taken.has(option.value);
  if (!base.some((group) => group.options.some(isTaken))) return base;
  return base.map((group) =>
    group.options.some(isTaken)
      ? { ...group, options: group.options.map((option) => (isTaken(option) ? { ...option, disabled: true } : option)) }
      : group,
  );
}

/**
 * Ordnet jeder GROUP-Zeile ihre Auswahlliste zu. Das spiegelt die Teilungs-Logik des Backends
 * (`fixedDivision`/`takenGroupUuids`), rechnet sie aber live nach: der Anwender wählt schon vor dem
 * abschließenden Recheck, also müssen sich die Einschränkungen sofort aus den aktuellen Zuordnungen
 * ergeben — nicht erst aus den Snapshot-Feldern des Reports.
 *
 * Pro ASC-Teilung (`ascDivisionId`) gilt: alle Zeilen zeigen in DIESELBE Teilung, und keine zwei
 * Zeilen dürfen dieselbe Gruppe belegen. Die Teilung ist festgelegt, sobald der Report sie kennt
 * (`divisionUuids`) oder eine Zeile eine Gruppe gewählt hat (`divisionOfGroup`).
 *
 * @param skipGroupOnly Rückfalliste (nur „überspringen"), wenn eine Zeile weder Klasse noch Teilung
 *   kennt — dann gibt es nichts sinnvoll Wählbares. `skipGroupOnly[0]` ist die Skip-Pseudo-Teilung.
 */
export function ascGroupOptionsByEntry(
  entries: AscUnresolvedEntity[],
  mappings: Record<string, string | null>,
  groupOptions: AscGroupOptions,
  skipGroupOnly: GroupedGroupOption[],
): Map<string, AscGroupEntryOptions> {
  const skipGroup = skipGroupOnly[0];
  const groups = entries.filter((entry) => entry.kind === 'GROUP');

  // Je ASC-Teilung sammeln, worauf sie festgelegt ist (Teilungen) und welche Gruppen schon belegt
  // sind (aus dem Report UND aus den live getroffenen Auswahlen).
  const divisionsByAsc = new Map<string, Set<string>>();
  const takenByAsc = new Map<string, Set<string>>();

  for (const entry of groups) {
    if (!entry.ascDivisionId) continue;
    const divisions = bucket(divisionsByAsc, entry.ascDivisionId);
    const taken = bucket(takenByAsc, entry.ascDivisionId);

    for (const divisionUuid of entry.divisionUuids ?? []) divisions.add(divisionUuid);
    for (const groupUuid of entry.takenGroupUuids ?? []) taken.add(groupUuid);

    const chosen = chosenGroup(mappings, entry.ascId);
    if (chosen) {
      taken.add(chosen);
      const divisionUuid = groupOptions.divisionOfGroup.get(chosen);
      if (divisionUuid) divisions.add(divisionUuid);
    }
  }

  const result = new Map<string, AscGroupEntryOptions>();
  for (const entry of groups) {
    const divisions = entry.ascDivisionId ? divisionsByAsc.get(entry.ascDivisionId) : undefined;
    const taken = entry.ascDivisionId ? takenByAsc.get(entry.ascDivisionId) : undefined;
    const own = chosenGroup(mappings, entry.ascId);

    let base: GroupedGroupOption[];
    let divisionName: string | undefined;
    if (divisions && divisions.size === 1) {
      const [only] = divisions;
      base = groupOptions.byDivision.get(only) ?? skipGroupOnly;
      divisionName = groupOptions.divisionName.get(only);
    } else if (divisions && divisions.size > 1) {
      base = mergeDivisions(divisions, groupOptions, skipGroup);
    } else if (entry.classUuid && groupOptions.byClass.has(entry.classUuid)) {
      base = groupOptions.byClass.get(entry.classUuid)!;
    } else {
      base = skipGroupOnly;
    }

    const options = disableTaken(base, taken, own);
    result.set(entry.ascId, divisionName ? { options, divisionName } : { options });
  }

  return result;
}
